import { ReactNode, useCallback, useMemo } from "react";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import {
    ScheduleApplicationStatus,
    ScheduleSourceType,
} from "@common/types/domains/accounting/schedule";
import type { TransactionScheduleCallbacks } from "@hooks/view/useViewTableMeta";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScheduleChange } from "@components/accounting/schedules/change/useScheduleChange";
import {
    ScheduleSpecificDateApplyRequest,
    ScheduleSpecificDateApplyResult,
} from "@components/accounting/schedules/manual/scheduleSpecificDateApply";
import { useScheduleSpecificDateApply } from "@components/accounting/schedules/manual/useScheduleSpecificDateApply";
import {
    opensScheduleModalBeforeApply,
    SchedulePickerTemplateWithAnchors,
} from "@components/accounting/schedules/scheduleApplyConfirmHelpers";
import { useOpenScheduleJournalEntries } from "@components/accounting/schedules/useOpenScheduleJournalEntries";
import { useScheduleLifecycleRequests } from "@components/accounting/schedules/useScheduleLifecycleRequests";
import {
    ShowLockedPeriods,
    useScheduleLockedPeriodsDialog,
} from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import { useApplyScheduleWithLinkedConflict } from "@components/accounting/schedules/linkedConflict/useApplyScheduleWithLinkedConflict";
import {
    buildTransactionSchedulePayload,
    buildTransactionSpecificDateRequest,
} from "./transactionSchedulePayload";
import { useRemoveTransactionSchedule } from "./useRemoveTransactionSchedule";

type SchedulePickerTransaction = Pick<
    TransactionListItem,
    | "id"
    | "entity"
    | "schedule"
    | "matchedSchedules"
    | "dateOnly"
    | "displayDescription"
    | "bankDescription"
> & {
    amount: number;
};

type TransactionSchedulePickerTemplate = Pick<
    SchedulePickerTemplateWithAnchors,
    | "id"
    | "name"
    | "hasSpecificDateAnchor"
    | "revenueSubMode"
    | "expenseSubMode"
>;

/**
 * The apply, remove and lifecycle wiring a transaction schedule picker needs,
 * shared by the grid cell and the side panel so the two cannot drift. Each
 * surface supplies its own templates and renders the picker itself.
 */
export function useTransactionSchedulePicker(options: {
    transaction: SchedulePickerTransaction;
    isReadonlyOrLocked: boolean;
    scheduleCallbacks?: TransactionScheduleCallbacks;
    showLockedPeriods?: ShowLockedPeriods;
}) {
    const { transaction, isReadonlyOrLocked, scheduleCallbacks } = options;
    const requestLifecycleAction = scheduleCallbacks?.onRequestLifecycleAction;
    const onCreateSchedule = scheduleCallbacks?.onCreateSchedule;
    const { toast, error } = useToaster();
    const { applyWithLinkedConflict, linkedConflictModal, isApplying } =
        useApplyScheduleWithLinkedConflict({
            showLockedPeriods: options.showLockedPeriods,
        });
    const showLockedPeriods = useScheduleLockedPeriodsDialog(
        options.showLockedPeriods,
    );
    const schedule = transaction.schedule;

    const applied = useMemo(
        () =>
            schedule
                ? {
                      name:
                          schedule.status === ScheduleApplicationStatus.draft
                              ? `${schedule.scheduleName} (suggested)`
                              : schedule.scheduleName,
                      termEnd: schedule.termEnd,
                      remainingAmount: schedule.remainingAmount,
                      status: schedule.status,
                  }
                : null,
        [schedule],
    );

    const runApply = useCallback(
        async (applyOptions: {
            templateId: string;
            specificDateApply?: ScheduleSpecificDateApplyResult;
        }): Promise<boolean> => {
            try {
                const result = await applyWithLinkedConflict({
                    templateId: applyOptions.templateId,
                    payload: buildTransactionSchedulePayload({
                        entityId: transaction.entity.id,
                        transactionId: transaction.id,
                        amount: transaction.amount,
                        specificDateApply: applyOptions.specificDateApply,
                    }),
                });
                // The conflict modal finishes the apply; not a failure.
                if (result.outcome === "awaitingChoice") {
                    return true;
                }
                toast("Schedule applied");
                return true;
            } catch (applyError) {
                if (!showLockedPeriods(applyError)) {
                    error(
                        applyError instanceof Error
                            ? applyError.message
                            : "Failed to apply schedule",
                    );
                }
                return false;
            }
        },
        [
            applyWithLinkedConflict,
            error,
            showLockedPeriods,
            toast,
            transaction.amount,
            transaction.entity.id,
            transaction.id,
        ],
    );

    const applyPending = useCallback(
        (
            pending: ScheduleSpecificDateApplyRequest,
            result: ScheduleSpecificDateApplyResult,
        ) =>
            runApply({
                templateId: pending.templateId,
                specificDateApply: result,
            }),
        [runApply],
    );
    const { open: openSpecificDate, modal: specificDateModal } =
        useScheduleSpecificDateApply({ apply: applyPending, isApplying });

    const applySchedule = useCallback(
        async (
            template: TransactionSchedulePickerTemplate,
        ): Promise<boolean> => {
            if (isReadonlyOrLocked) {
                return false;
            }
            if (opensScheduleModalBeforeApply(template)) {
                return await openSpecificDate(
                    buildTransactionSpecificDateRequest({
                        template,
                        entityId: transaction.entity.id,
                        transactionId: transaction.id,
                        amount: transaction.amount,
                        label:
                            transaction.displayDescription ??
                            transaction.bankDescription,
                        dateOnly: transaction.dateOnly,
                    }),
                );
            }
            return await runApply({ templateId: template.id });
        },
        [
            isReadonlyOrLocked,
            openSpecificDate,
            runApply,
            transaction.amount,
            transaction.bankDescription,
            transaction.dateOnly,
            transaction.displayDescription,
            transaction.entity.id,
            transaction.id,
        ],
    );

    const { onChangeSchedule, onResumeSchedule } = useScheduleLifecycleRequests(
        {
            applicationId: schedule?.scheduleApplicationId ?? null,
            status: schedule?.status ?? null,
            entityId: transaction.entity.id,
            sourceType: ScheduleSourceType.transaction,
            requestLifecycleAction: isReadonlyOrLocked
                ? undefined
                : requestLifecycleAction,
        },
    );
    const { removeCurrent, isRemoving } = useRemoveTransactionSchedule(
        transaction,
        {
            showLockedPeriods: options.showLockedPeriods,
        },
    );
    const onViewSchedule = useOpenScheduleJournalEntries(
        schedule
            ? {
                  templateId: schedule.scheduleTemplateId,
                  scheduleApplicationId: schedule.scheduleApplicationId,
              }
            : null,
    );
    const { onSelect, onClear, confirmModal } = useScheduleChange({
        source: "transaction",
        current: schedule
            ? {
                  scheduleApplicationId: schedule.scheduleApplicationId,
                  scheduleTemplateId: schedule.scheduleTemplateId,
                  scheduleName: schedule.scheduleName,
              }
            : null,
        applySchedule,
        removeCurrent,
    });

    const overlays: ReactNode = (
        <>
            {confirmModal}
            {linkedConflictModal}
            {specificDateModal}
        </>
    );

    const matchedSchedules = schedule
        ? []
        : (transaction.matchedSchedules ?? []);

    return {
        matchedSchedules,
        pickerProps: {
            onViewSchedule,
            onCreate: onCreateSchedule
                ? () =>
                      onCreateSchedule({
                          transactionId: transaction.id,
                          entityId: transaction.entity.id,
                      })
                : undefined,
            applied,
            readonly: isReadonlyOrLocked,
            isDisabled: isApplying || isRemoving,
            onSelect,
            onClear: isReadonlyOrLocked ? undefined : onClear,
            onChangeSchedule,
            onResumeSchedule,
        },
        overlays,
    };
}
